import React from "react";
import { Autocomplete, IconButton, TextField, Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useTranslation } from "react-i18next";
import Button from "components/atoms/Button";
import type { DriverOption } from "components/CardDriverArea/coverageAreaDrivers";
import type { SlotRow } from "../CardDriverAreaMap/useMapAreaDriverSelection";
import * as Styled from "./styles";
import {
  getMapAreaSlotDeleteIconButtonSx,
  mapAreaApplyButtonSx,
  mapAreaSlotAutocompleteListboxProps,
  mapAreaSlotAutocompleteSx,
  mapAreaTextLinkButtonSx,
} from "./muiSx";
import { useMapDriversInlineForm } from "./useMapDriversInlineForm";

interface MapDriversInlineFormProps {
  slotRows: SlotRow[];
  options: DriverOption[];
  canAddSlot: boolean;
  onSlotChange: (key: string, value: DriverOption | null) => void;
  onAddSlot: () => void;
  onRemoveSlot: (key: string) => void;
  onSave: () => void | Promise<void>;
  onCancel: () => void;
}

function MapDriversInlineForm({
  slotRows,
  options,
  canAddSlot,
  onSlotChange,
  onAddSlot,
  onRemoveSlot,
  onSave,
  onCancel,
}: MapDriversInlineFormProps) {
  const { t } = useTranslation();
  const { optionsForSlot } = useMapDriversInlineForm({ slotRows, options });

  return (
    <Styled.MapInlineEditSection>
      <Styled.MapAreaEditColumn>
        <Typography variant="body2" color="text.secondary">
          {t("addDriversForCoverageInstruction")}
        </Typography>

        {slotRows.map((row, index) => (
          <Styled.MapDriverSelectRow key={row.key}>
            <Autocomplete
              disablePortal
              value={row.value}
              options={optionsForSlot[index] ?? options}
              size="small"
              onChange={(_, value) => onSlotChange(row.key, value)}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              ListboxProps={mapAreaSlotAutocompleteListboxProps}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("deliveryPersonSlotLabel", { number: index + 1 })}
                  placeholder={t("selectDriver")}
                  InputLabelProps={{ shrink: true }}
                />
              )}
              sx={mapAreaSlotAutocompleteSx}
            />
            <IconButton
              aria-label={t("delete")}
              onClick={() => onRemoveSlot(row.key)}
              size="small"
              sx={getMapAreaSlotDeleteIconButtonSx(Boolean(row.value))}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Styled.MapDriverSelectRow>
        ))}

        <Button
          variant="text"
          color="primary"
          onClick={onAddSlot}
          disabled={!canAddSlot}
          sx={mapAreaTextLinkButtonSx}
        >
          {t("addAnotherDriver")}
        </Button>

        <Styled.MapAreaButtonsRow>
          <Button
            type="button"
            color="inherit"
            variant="outlined"
            onClick={onCancel}
          >
            {t("cancelAction").toUpperCase()}
          </Button>
          <Button
            type="button"
            variant="contained"
            onClick={() => {
              Promise.resolve(onSave()).catch(() => {});
            }}
            sx={mapAreaApplyButtonSx}
          >
            {t("toApply").toUpperCase()}
          </Button>
        </Styled.MapAreaButtonsRow>
      </Styled.MapAreaEditColumn>
    </Styled.MapInlineEditSection>
  );
}

export default MapDriversInlineForm;
